"use client"

import { useEffect, useState, useMemo } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Button,
  Skeleton,
} from "@mui/material"
import {
  History as HistoryIcon,
  CheckCircle,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  ChevronLeft,
  ChevronRight,
  RestartAlt as ResetIcon,
} from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux"
import { deleteSale } from "../../redux/actions/sales"
import { toast } from "react-toastify"
import moment from "moment"
import { fetchAllShops, selectShop } from "../../redux/actions/shop"
import ProductDetailsDialog from "../../components/ProductDetailsDialog"
import { generateReceipt } from "../../utils/receiptGenerator"
import { useInvoiceNumber } from "../../hooks/useInvoiceNumber"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { useTheme } from "@mui/material/styles"
import { tokens } from "../../theme"
import useSalesBatch from "../../hooks/useSalesBatch"

const ROWS_PER_PAGE = 50

const SkeletonRows = ({ count = 5, columns = 7 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: columns }).map((_, j) => (
          <TableCell key={j}>
            <Skeleton variant="text" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
)

const VenteEnCash = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.user)
  const { shops, selectedShop } = useSelector((state) => state.shop)
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const [openHistory, setOpenHistory] = useState(false)
  const [selectedSale, setSelectedSale] = useState(null)
  const [openProductDetails, setOpenProductDetails] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  const { getInvoiceNumber } = useInvoiceNumber()

  const electronicShops = shops.filter((shop) => shop.enum === "electronics" || shop.type === "electronics")

  const currentShopId = useMemo(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      return selectedShop?._id || "all"
    }
    return typeof user?.shop === "object" ? user.shop._id : user?.shop
  }, [user, selectedShop])

  const filters = useMemo(() => ({
    paymentMethod: "cash",
    paymentStatus: "paid",
    startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : undefined,
    endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
    search: searchQuery || undefined,
  }), [startDate, endDate, searchQuery])

  const { data: cashSales, isLoading, page, setPage, total, totalPages, invalidateCache } = useSalesBatch({
    shopId: currentShopId,
    filters,
    limit: ROWS_PER_PAGE,
  })

  useEffect(() => {
    if (user?.role === "superadmin" || user?.role === "supervisor") {
      dispatch(fetchAllShops())
    }
  }, [dispatch, user])

  const handleShopChange = (event) => {
    const shopId = event.target.value
    if (shopId === "all") {
      dispatch(selectShop(null))
    } else {
      const shop = shops.find((s) => s._id === shopId)
      dispatch(selectShop(shop))
    }
  }

  const handleSearchSubmit = () => {
    setSearchQuery(searchInput)
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit()
    }
  }

  const handleResetFilters = () => {
    setSearchQuery("")
    setSearchInput("")
    setStartDate(null)
    setEndDate(null)
  }

  const handlePrint = async (sale) => {
    try {
      const shopId = sale.shop?._id || sale.shop
      const invoiceNumber = await getInvoiceNumber(shopId)
      const doc = await generateReceipt(sale, invoiceNumber)
      doc.save(`facture-${invoiceNumber}.pdf`)
    } catch (error) {
      toast.error("Erreur lors de l'impression de la facture")
      console.error("Print error:", error)
    }
  }

  const handleDelete = async (saleId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette vente ?")) {
      try {
        await dispatch(deleteSale(saleId))
        toast.success("Vente supprimée avec succès")
        invalidateCache()
        setPage(1)
      } catch (error) {
        toast.error(error.message || "Erreur lors de la suppression")
      }
    }
  }

  const startRecord = (page - 1) * ROWS_PER_PAGE + 1
  const endRecord = Math.min(page * ROWS_PER_PAGE, total)

  return (
    <Box m="20px">
      <Card
        sx={{
          mb: 4,
          mt: 2,
          backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.primary[100],
          color: theme.palette.mode === "dark" ? colors.grey[100] : "white",
          boxShadow: `0 8px 32px rgba(${colors.primary[900]}, 0.3)`,
          borderRadius: 3,
          border: "1px solid rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(10px)",
          transition: "all 0.4s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: `0 16px 48px rgba(${colors.primary[900]}, 0.4)`,
          },
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ opacity: 0.9, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}
          >
            Total des ventes en cash
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: "bold", fontSize: "2.5rem" }}>
            {total.toLocaleString()} <span style={{ fontSize: "1rem" }}>ventes</span>
          </Typography>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: theme.palette.mode === "dark" ? "text.primary" : "white" }}
        >
          Ventes payées ({total || 0})
        </Typography>

        {(user.role === "superadmin" || user.role === "supervisor") && (
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Sélectionner boutique</InputLabel>
            <Select
              value={selectedShop ? selectedShop._id : "all"}
              onChange={handleShopChange}
              label="Sélectionner boutique"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary[600] },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary[700] },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary[600] },
              }}
            >
              <MenuItem value="all">Toutes les boutiques</MenuItem>
              {electronicShops.map((shop) => (
                <MenuItem key={shop._id} value={shop._id}>
                  {shop.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: theme.palette.background.paper,
          backdropFilter: "blur(10px)",
          border: `1px solid rgba(${colors.primary[600]}, 0.1)`,
          boxShadow: `0 8px 32px rgba(${colors.primary[900]}, 0.15)`,
        }}
      >
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            placeholder="Recherche par client"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchSubmit}
            sx={{
              flexGrow: 1,
              minWidth: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                transition: "all 0.3s ease",
                "& fieldset": { borderColor: colors.grey[300] },
                "&:hover fieldset": { borderColor: colors.primary[600] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
              },
            }}
          />

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Date de début"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": { borderColor: colors.grey[300] },
                      "&:hover fieldset": { borderColor: colors.primary[600] },
                      "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
                    },
                  },
                },
              }}
            />
            <DatePicker
              label="Date de fin"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": { borderColor: colors.grey[300] },
                      "&:hover fieldset": { borderColor: colors.primary[600] },
                      "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>

          <Tooltip title="Réinitialiser les filtres">
            <IconButton onClick={handleResetFilters} sx={{ color: colors.primary[500] }}>
              <ResetIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          overflow: "auto",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          backgroundColor: "background.paper",
          maxHeight: "calc(100vh - 420px)",
          "& .MuiTableRow-root": {
            "&:nth-of-type(odd)": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
            },
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
            },
          },
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(81, 81, 81, 0.5)" : "rgba(224, 224, 224, 1)"}`,
            color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiTableHead-root": {
            "& .MuiTableRow-root": {
              backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 30, 30)" : "rgb(250, 250, 250)",
              "&:hover": {
                backgroundColor: theme.palette.mode === "dark" ? "rgb(30, 30, 30)" : "rgb(250, 250, 250)",
              },
              "& .MuiTableCell-root": {
                color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
                fontWeight: 600,
                backgroundColor: "transparent",
              },
            },
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Montant Total</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Méthode de paiement</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Effectué par</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows count={10} columns={7} />
            ) : cashSales.length > 0 ? (
              cashSales.map((sale) => (
                <TableRow key={sale._id}>
                  <TableCell>{moment(sale.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                  <TableCell>{sale.client_name}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: colors.greenAccent[500] }}>
                    {sale.total_amount?.toLocaleString()} Fbu
                  </TableCell>
                  <TableCell>{sale.payment_method}</TableCell>
                  <TableCell>
                    {sale.payment_status === "paid" ? (
                      <Chip label="Payé" color="success" size="small" sx={{ fontWeight: 600 }} />
                    ) : sale.payment_status === "partial" ? (
                      <Chip label="Partiel" color="warning" size="small" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Chip label="Non payé" color="error" size="small" sx={{ fontWeight: 600 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Historique des paiements">
                        <IconButton
                          onClick={() => { setSelectedSale(sale); setOpenHistory(true) }}
                          size="small"
                          sx={{
                            color: colors.grey[100],
                            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)", color: colors.primary[100] },
                          }}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {user.role !== "supervisor" && (
                        <>
                          <Tooltip title="Voir les détails">
                            <IconButton
                              onClick={() => { setSelectedSale(sale); setOpenProductDetails(true) }}
                              size="small"
                              sx={{
                                color: colors.grey[100],
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)", color: colors.primary[100] },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimer la facture">
                            <IconButton
                              onClick={() => handlePrint(sale)}
                              size="small"
                              sx={{
                                color: colors.grey[100],
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)", color: colors.primary[100] },
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user.role === "superadmin" && (
                            <Tooltip title="Supprimer">
                              <IconButton
                                onClick={() => handleDelete(sale._id)}
                                size="small"
                                sx={{
                                  color: colors.grey[100],
                                  "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.1)", color: colors.redAccent[500] },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: colors.grey[100] }}>{sale.user?.name}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: colors.grey[500] }}>
                  <Typography variant="body2">Aucune vente trouvée</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
        px={1}
      >
        <Typography variant="body2" color="text.secondary">
          {total > 0 ? `Affichage de ${startRecord}–${endRecord} sur ${total.toLocaleString()} ventes` : "Aucune vente"}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            size="small"
            startIcon={<ChevronLeft />}
            disabled={page <= 1 || isLoading}
            onClick={() => setPage(page - 1)}
            sx={{ textTransform: "none" }}
          >
            Précédent
          </Button>
          <Typography variant="body2" sx={{ mx: 1 }}>
            Page {page} sur {totalPages || 1}
          </Typography>
          <Button
            size="small"
            endIcon={<ChevronRight />}
            disabled={page >= totalPages || isLoading}
            onClick={() => setPage(page + 1)}
            sx={{ textTransform: "none" }}
          >
            Suivant
          </Button>
        </Box>
      </Box>

      <ProductDetailsDialog
        open={openProductDetails}
        onClose={() => { setOpenProductDetails(false); setSelectedSale(null) }}
        products={selectedSale?.products || []}
      />
    </Box>
  )
}

export default VenteEnCash
